import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X } from 'lucide-react';

/**
 * Expanding search dock — collapses to search icon, expands to full input.
 * Inspired by moumensoliman/expanding-search-dock-shadcnui (21st.dev).
 * Adapted for Orbis dark theme + brand tokens.
 */
export default function ExpandingSearch({ onSearch, projects = [], onNavigate }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Filter projects by name
  const results = query.trim().length > 0
    ? projects.filter(p => p.name?.toLowerCase().includes(query.toLowerCase())).slice(0, 5)
    : [];

  // Auto-focus input on expand
  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => inputRef.current?.focus(), 150);
      return () => clearTimeout(t);
    } else {
      setQuery('');
    }
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Keyboard shortcut: ⌘K or Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (results.length > 0 && onNavigate) {
      onNavigate(results[0]);
      setIsOpen(false);
    } else if (onSearch) {
      onSearch(query);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <AnimatePresence mode="wait">
        {!isOpen ? (
          <motion.button
            key="icon"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.15 }}
            onClick={() => setIsOpen(true)}
            className="flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
            style={{ background: 'none', border: 'none', padding: 4 }}
            title="Search (⌘K)"
          >
            <Search className="w-3.5 h-3.5" style={{ color: 'var(--orbis-t3)' }} />
          </motion.button>
        ) : (
          <motion.form
            key="form"
            initial={{ width: 28, opacity: 0.9 }}
            animate={{ width: 280, opacity: 1 }}
            exit={{ width: 28, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            onSubmit={handleSubmit}
            className="flex items-center"
            style={{
              height: 32,
              borderRadius: 100,
              background: 'var(--orbis-s1)',
              border: '0.5px solid var(--orbis-s3)',
              backdropFilter: 'blur(12px)',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Search className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--orbis-t4)', marginLeft: 10 }} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search projects..."
              style={{
                flex: 1, background: 'transparent', border: 'none', outline: 'none',
                color: 'var(--orbis-t1)', fontSize: 12, fontFamily: 'var(--orbis-font)',
                padding: '0 8px', minWidth: 0,
              }}
            />
            <motion.button
              type="button"
              onClick={() => setIsOpen(false)}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15 }}
              className="cursor-pointer hover:opacity-80 transition-opacity shrink-0"
              style={{ background: 'none', border: 'none', padding: 4, marginRight: 6 }}
            >
              <X className="w-3 h-3" style={{ color: 'var(--orbis-t4)' }} />
            </motion.button>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Search Results Dropdown */}
      <AnimatePresence>
        {isOpen && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'absolute', top: 38, right: 0, width: 280,
              background: 'var(--orbis-s1)', border: '0.5px solid var(--orbis-s2)',
              borderRadius: 10, overflow: 'hidden', zIndex: 50,
              boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
            }}
          >
            {results.map((project, i) => (
              <button
                key={project.id}
                onClick={() => { onNavigate?.(project); setIsOpen(false); }}
                className="flex items-center gap-2.5 w-full cursor-pointer hover:bg-[var(--orbis-s2)] transition-colors"
                style={{ background: 'none', border: 'none', padding: '10px 14px', textAlign: 'left' }}
              >
                <div style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: project.magic_mode ? 'var(--orbis-blue)' : project.clone_mode ? 'var(--orbis-amber)' : 'var(--orbis-t4)',
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {project.name}
                  </p>
                  <p style={{ fontSize: 10, color: 'var(--orbis-t4)', fontFamily: 'var(--orbis-mono)' }}>
                    {project.magic_mode ? 'Magic' : project.clone_mode ? 'Clone' : 'Classic'} · {project.magic_state?.current_stage || 'draft'}
                  </p>
                </div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
